import { useLocation, useNavigate } from "react-router-dom";
import Layout from "../../Layout/Layout";
import useUserStore from "../../zustand-stores/UserStore";
import { useGetLevelDetails } from "../../Controllers/DashboardController";
import { FaChevronLeft, FaInfoCircle, FaTrophy, FaStar } from "react-icons/fa";

export default function LeaderboardInfo() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useUserStore();
  const { data: userLevelData } = useGetLevelDetails();
  const userLevelDetails = userLevelData?.level_details || null;
  const { levelDetails } = location.state || {};

  if (!levelDetails) {
    return (
      <Layout>
        <div className="container py-5 text-center">
          <div
            style={{
              backgroundColor: "rgba(26, 42, 55, 0.6)",
              padding: "40px",
              borderRadius: "24px",
              border:
                "1px solid color-mix(in srgb, var(--Primary-500), transparent 80%)",
              backdropFilter: "blur(10px)",
            }}
          >
            <FaInfoCircle
              size={48}
              color="var(--Primary-500)"
              className="mb-3"
            />
            <h2 style={{ color: "#FFFFFF", marginBottom: "16px" }}>
              No Information Available
            </h2>
            <p
              style={{
                color: "rgba(230, 236, 241, 0.7)",
                marginBottom: "32px",
              }}
            >
              Please access this page from the Leaderboards to see the level
              thresholds.
            </p>
            <button
              onClick={() => navigate("/leaderboards")}
              className="btn btn-primary px-4 py-2"
              style={{
                borderRadius: "12px",
                background:
                  "linear-gradient(135deg, var(--Primary-500) 0%, var(--Primary-600) 100%)",
                border: "none",
                fontWeight: 600,
              }}
            >
              Go to Leaderboards
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        {/* Header Section */}
        <div className="d-flex flex-column gap-4 mb-5">
          <div
            className="d-inline-flex align-items-center gap-2"
            style={{
              cursor: "pointer",
              color: "var(--Primary-500)",
              fontSize: "14px",
              fontWeight: 500,
              width: "fit-content",
              transition: "all 0.2s ease",
            }}
            onClick={() => navigate("/leaderboards")}
          >
            <FaChevronLeft size={12} />
            <span>Back to Leaderboards</span>
          </div>

          <div className="d-flex justify-content-between align-items-end flex-wrap gap-3">
            <div>
              <h1
                style={{
                  fontSize: "32px",
                  fontWeight: 800,
                  color: "#FFFFFF",
                  margin: 0,
                  letterSpacing: "-0.5px",
                }}
              >
                Level Progression
              </h1>
              <p
                style={{
                  color: "rgba(230, 236, 241, 0.6)",
                  margin: "8px 0 0 0",
                }}
              >
                Understand how you rank and progress through levels.
              </p>
            </div>
          </div>
        </div>

        <div className="row g-4 mb-5">
          {/* Info Cards */}
          <div className="col-md-6">
            <div
              style={{
                backgroundColor: "rgba(26, 42, 55, 0.4)",
                padding: "24px",
                borderRadius: "20px",
                border: "1px solid rgba(255, 255, 255, 0.05)",
                height: "100%",
                transition: "transform 0.3s ease",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.transform = "translateY(-5px)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.transform = "translateY(0)")
              }
            >
              <div className="d-flex align-items-center gap-3 mb-3">
                <div
                  style={{
                    backgroundColor: "rgba(255, 193, 7, 0.15)",
                    padding: "12px",
                    borderRadius: "12px",
                  }}
                >
                  <FaStar color="#f5ca41" size={20} />
                </div>
                <h3
                  style={{
                    fontSize: "20px",
                    fontWeight: 700,
                    color: "#FFFFFF",
                    margin: 0,
                  }}
                >
                  Points System
                </h3>
              </div>
              <p
                style={{
                  color: "rgba(230, 236, 241, 0.7)",
                  lineHeight: "1.6",
                  fontSize: "15px",
                  margin: 0,
                }}
              >
                Points are earned one by one as you complete your habits, tasks,
                reflections, and goals. Each achievement adds 1 XP to your
                progress. Once you've gathered enough XP to reach your next
                level's target, you'll level up automatically.
              </p>
            </div>
          </div>

          <div className="col-md-6">
            <div
              style={{
                backgroundColor: "rgba(26, 42, 55, 0.4)",
                padding: "24px",
                borderRadius: "20px",
                border: "1px solid rgba(255, 255, 255, 0.05)",
                height: "100%",
                transition: "transform 0.3s ease",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.transform = "translateY(-5px)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.transform = "translateY(0)")
              }
            >
              <div className="d-flex align-items-center gap-3 mb-3">
                <div
                  style={{
                    backgroundColor:
                      "color-mix(in srgb, var(--Primary-500), transparent 85%)",
                    padding: "12px",
                    borderRadius: "12px",
                  }}
                >
                  <FaTrophy color="var(--Primary-500)" size={20} />
                </div>
                <h3
                  style={{
                    fontSize: "20px",
                    fontWeight: 700,
                    color: "#FFFFFF",
                    margin: 0,
                  }}
                >
                  Leveling Up
                </h3>
              </div>
              <p
                style={{
                  color: "rgba(230, 236, 241, 0.7)",
                  lineHeight: "1.6",
                  fontSize: "15px",
                  margin: 0,
                }}
              >
                Your level tells the story of your consistency. It's prominently
                displayed on your avatar. Below is a comprehensive list of all
                level thresholds. Keep pushing to reach level 100!
              </p>
            </div>
          </div>
        </div>

        {/* Level Grid Section */}
        <div
          style={{
            backgroundColor: "#16202A",
            padding: "24px",
            borderRadius: "20px",
            border: "1px solid rgba(42, 64, 81, 0.4)",
            boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2
              style={{
                fontSize: "20px",
                fontWeight: 700,
                color: "#FFFFFF",
                margin: 0,
              }}
            >
              Level Thresholds
            </h2>
            <div
              style={{
                color: "rgba(230, 236, 241, 0.4)",
                fontSize: "13px",
                fontWeight: 500,
              }}
            >
              {levelDetails.length} Levels Total
            </div>
          </div>

          <div
            className="row row-cols-2 row-cols-sm-3 row-cols-md-4 row-cols-lg-5 row-cols-xl-6 g-2"
            style={{
              overflowY: "auto",
              paddingRight: "8px",
              scrollbarWidth: "thin",
              scrollbarColor: "var(--Primary-500) rgba(0,0,0,0.1)",
            }}
          >
            {levelDetails.map((threshold) => {
              const currentLvl =
                userLevelDetails?.current_level || user?.level || 1;
              const isUserLevel = threshold.level === currentLvl;
              return (
                <div key={threshold.level} className="col">
                  <div
                    style={{
                      padding: "8px 12px",
                      borderRadius: "10px",
                      backgroundColor: isUserLevel
                        ? "color-mix(in srgb, var(--Primary-500), transparent 85%)"
                        : "rgba(255, 255, 255, 0.02)",
                      border: isUserLevel
                        ? "1px solid var(--Primary-500)"
                        : "1px solid rgba(255, 255, 255, 0.04)",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      transition: "all 0.2s ease",
                      cursor: "default",
                    }}
                    onMouseEnter={(e) => {
                      if (!isUserLevel) {
                        e.currentTarget.style.backgroundColor =
                          "rgba(255, 255, 255, 0.05)";
                        e.currentTarget.style.borderColor =
                          "color-mix(in srgb, var(--Primary-500), transparent 70%)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isUserLevel) {
                        e.currentTarget.style.backgroundColor =
                          "rgba(255, 255, 255, 0.02)";
                        e.currentTarget.style.borderColor =
                          "rgba(255, 255, 255, 0.04)";
                      }
                    }}
                  >
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: 700,
                        color: isUserLevel
                          ? "var(--Primary-500)"
                          : "rgba(230, 236, 241, 0.4)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Lvl {threshold.level}
                    </span>
                    <div
                      style={{
                        flex: 1,
                        borderBottom: isUserLevel
                          ? "1px dotted color-mix(in srgb, var(--Primary-500), transparent 60%)"
                          : "1px dotted rgba(255, 255, 255, 0.1)",
                        margin: "0 8px",
                        alignSelf: "flex-end",
                        marginBottom: "4px",
                      }}
                    />
                    <span
                      style={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: isUserLevel
                          ? "#FFFFFF"
                          : "rgba(230, 236, 241, 0.7)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {threshold.xp_required}{" "}
                      <span style={{ fontSize: "10px", opacity: 0.6 }}>XP</span>
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Layout>
  );
}
